/**
 * Operations module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Descripción — narrative description of how value is delivered.
 *  02 Servicios — catalogue with price + margin + duration, auto totals.
 *  03 Procesos clave — operational processes / SOPs.
 *  04 Proveedores y partners — supplier/partner narrative.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Trash2,
  FileText,
  Boxes,
  Workflow,
  Truck,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";

const empty = { description: "", services: [], key_processes: "", suppliers_partners: "" };

export default function OperationsModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("description");
  useEffect(() => {
    setV((prev) => {
    const next = { ...empty, ...(data || {}) };
    try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
    return next;
  });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const add = () =>
    setV((p) => ({ ...p, services: [...(p.services || []), { name: "", description: "", price: 0, margin: 0, duration: "" }] }));
  const update = (i, k, val) =>
    setV((p) => ({ ...p, services: p.services.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const remove = (i) =>
    setV((p) => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    const list = v.services || [];
    const totalRev = list.reduce((s, x) => s + (parseFloat(x.price) || 0), 0);
    const weightedMargin = totalRev > 0
      ? list.reduce((s, x) => s + ((parseFloat(x.price) || 0) * (parseFloat(x.margin) || 0)) / totalRev, 0)
      : 0;
    return { count: list.length, totalRev, avgMargin: weightedMargin };
  }, [v.services]);

  const suggestions = lang === "es"
    ? [
        "Diseña un catálogo de servicios con precios y márgenes para este negocio",
        "Lista los procesos clave necesarios para entregar el servicio",
        "Sugiere partners y proveedores estratégicos",
        "Define SOPs para los procesos críticos",
      ]
    : [
        "Design a services catalog with prices and margins",
        "List the key processes to deliver the service",
        "Suggest strategic partners and suppliers",
        "Define SOPs for critical processes",
      ];

  const tabs = [
    { key: "description", label: lang === "es" ? "Descripción" : "Description", icon: FileText },
    { key: "services",    label: lang === "es" ? "Servicios"   : "Services",    icon: Boxes },
    { key: "processes",   label: lang === "es" ? "Procesos clave" : "Key processes", icon: Workflow },
    { key: "suppliers",   label: lang === "es" ? "Proveedores · Partners" : "Suppliers · Partners", icon: Truck },
  ];

  return (
    <>
      <SectionCard
        title={t("module.operations")}
        subtitle={lang === "es" ? "Cómo entregas valor: descripción, catálogo, procesos y red de partners." : "How you deliver value: description, catalog, processes and partner network."}
        action={<div className="flex items-center gap-3"><AIBadge /><AIAssistant module="operations" context={{ project: project?.name }} suggestions={suggestions} /></div>}
        testId="operations-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="ops-subtab" />

        {tab === "description" && (
          <div data-testid="ops-tab-description" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Describe el modelo operacional: fases del servicio, modelo de delivery, expectativas del cliente."
                : "Describe the operational model: service phases, delivery model, customer expectations."}
            </p>
            <FieldLabel>{t("ops.description")}</FieldLabel>
            <TextArea value={v.description} onChange={(x) => set("description", x)} rows={8} testId="ops-description-textarea" />
          </div>
        )}

        {tab === "services" && (
          <div data-testid="ops-tab-services" className="space-y-5">
            <div className="flex items-center justify-between gap-3">
              <Overline>{lang === "es" ? "Catálogo de servicios" : "Service catalog"}</Overline>
              <SecondaryButton onClick={add} testId="add-service-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("ops.add_service")}
              </SecondaryButton>
            </div>
            {(v.services || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin servicios. Añade el primero." : "No services yet. Add the first one."}
              </div>
            ) : (
              <div className="space-y-3">
                {v.services.map((s, i) => (
                  <div key={i} className="border border-[#E8E6E1] rounded-sm p-5 bg-white" data-testid={`service-${i}`}>
                    <div className="grid md:grid-cols-[2fr_120px_100px_140px] gap-3">
                      <div>
                        <FieldLabel>{t("ops.service_name")}</FieldLabel>
                        <TextInput value={s.name} onChange={(x) => update(i, "name", x)} testId={`service-${i}-name`} />
                      </div>
                      <div>
                        <FieldLabel>{t("ops.price")}</FieldLabel>
                        <TextInput type="number" value={s.price} onChange={(x) => update(i, "price", parseFloat(x) || 0)} testId={`service-${i}-price`} />
                      </div>
                      <div>
                        <FieldLabel>{t("ops.margin")}</FieldLabel>
                        <TextInput type="number" value={s.margin} onChange={(x) => update(i, "margin", parseFloat(x) || 0)} testId={`service-${i}-margin`} />
                      </div>
                      <div>
                        <FieldLabel>{t("ops.duration")}</FieldLabel>
                        <TextInput value={s.duration} onChange={(x) => update(i, "duration", x)} testId={`service-${i}-duration`} />
                      </div>
                    </div>
                    <div className="mt-3">
                      <FieldLabel>{t("ops.service_desc")}</FieldLabel>
                      <TextArea value={s.description} onChange={(x) => update(i, "description", x)} rows={2} testId={`service-${i}-desc`} />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button onClick={() => remove(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`service-${i}-remove`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                      </button>
                    </div>
                  </div>
                ))}
                <div className="border-t border-[#E8E6E1] pt-4 grid grid-cols-3 gap-4 font-mono text-sm" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
                  <div>
                    <Overline>{lang === "es" ? "Servicios" : "Services"}</Overline>
                    <div className="text-lg text-[#1A1A1A] mt-1">{totals.count}</div>
                  </div>
                  <div>
                    <Overline>{lang === "es" ? "Suma precios" : "Total prices"}</Overline>
                    <div className="text-lg text-[#1A1A1A] mt-1">€{totals.totalRev.toLocaleString()}</div>
                  </div>
                  <div>
                    <Overline>{lang === "es" ? "Margen medio" : "Avg margin"}</Overline>
                    <div className="text-lg text-[#B89154] mt-1">{totals.avgMargin.toFixed(1)}%</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "processes" && (
          <div data-testid="ops-tab-processes" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Describe los procesos críticos: onboarding, delivery, soporte. Es útil enumerarlos paso a paso."
                : "Describe critical processes: onboarding, delivery, support. Step-by-step lists work well."}
            </p>
            <FieldLabel>{t("ops.processes")}</FieldLabel>
            <TextArea value={v.key_processes} onChange={(x) => set("key_processes", x)} rows={10} testId="ops-processes-textarea" />
          </div>
        )}

        {tab === "suppliers" && (
          <div data-testid="ops-tab-suppliers" className="space-y-5">
            <p className="text-sm text-[#5C5B5A] max-w-2xl">
              {lang === "es"
                ? "Quiénes son tus proveedores y partners estratégicos. Para el detalle por país, ve al módulo Operaciones Especializadas."
                : "Your strategic suppliers and partners. For per-country detail, see the Specialised Operations module."}
            </p>
            <FieldLabel>{t("ops.suppliers")}</FieldLabel>
            <TextArea value={v.suppliers_partners} onChange={(x) => set("suppliers_partners", x)} rows={10} testId="ops-suppliers-textarea" />
          </div>
        )}
      </SectionCard>

    </>
  );
}
